import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  getDocs, 
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { Campaign, CampaignAnalytics, StudentSubmission } from '../types';

export class CampaignService {
  
  // Get all campaigns
  static async getAllCampaigns(includeInactive = true): Promise<Campaign[]> {
    try {
      const campaignsRef = collection(db, 'campaigns');
      let q = query(campaignsRef, orderBy('createdAt', 'desc'));
      
      // Don't filter by active status - show all campaigns including currently running ones
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Campaign));
    } catch (error) {
      console.error('Error getting campaigns:', error);
      return [];
    }
  }

  // Create new campaign
  static async createCampaign(campaign: Omit<Campaign, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> {
    try {
      const campaignsRef = collection(db, 'campaigns');
      const newCampaignRef = doc(campaignsRef);
      
      const campaignData: Campaign = {
        ...campaign,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp
      };
      
      await setDoc(newCampaignRef, campaignData);
      return newCampaignRef.id;
    } catch (error) {
      console.error('Error creating campaign:', error);
      return null;
    }
  }

  // Update existing campaign
  static async updateCampaign(campaignId: string, updates: Partial<Campaign>): Promise<boolean> {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating campaign:', error);
      return false;
    }
  }

  // Delete campaign
  static async deleteCampaign(campaignId: string): Promise<boolean> {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await deleteDoc(campaignRef);
      return true;
    } catch (error) {
      console.error('Error deleting campaign:', error);
      return false;
    }
  }

  // Get campaign by ID
  static async getCampaign(campaignId: string): Promise<Campaign | null> {
    try {
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
      if (!campaignDoc.exists()) return null;
      
      return {
        id: campaignDoc.id,
        ...campaignDoc.data()
      } as Campaign;
    } catch (error) {
      console.error('Error getting campaign:', error);
      return null;
    }
  }

  // Get campaign analytics
  static async getCampaignAnalytics(
    campaignId?: string,
    squadId?: string,
    dateRange?: { start: Date; end: Date }
  ): Promise<CampaignAnalytics[]> {
    try {
      let q = collection(db, 'studentFeedback');
      let constraints: any[] = [orderBy('submittedAt', 'desc')];
      
      if (squadId) {
        constraints.push(where('squadId', '==', squadId));
      }
      
      if (dateRange) {
        constraints.push(where('submittedAt', '>=', Timestamp.fromDate(dateRange.start)));
        constraints.push(where('submittedAt', '<=', Timestamp.fromDate(dateRange.end)));
      }
      
      const submissionsQuery = query(q, ...constraints);
      const snapshot = await getDocs(submissionsQuery);
      const submissions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudentSubmission));
      
      // Group by campaign
      const campaignMap = new Map<string, {
        campaign: Campaign | null;
        submissions: StudentSubmission[];
        responses: Map<string, any[]>;
      }>();
      
      // Get all campaigns mentioned in submissions
      const campaignIds = new Set<string>();
      submissions.forEach(submission => {
        // Handle case where blockB might be empty or undefined
        if (submission.blockB && Array.isArray(submission.blockB)) {
          submission.blockB.forEach(answer => {
            if (answer.campaignId) {
              campaignIds.add(answer.campaignId);
            }
          });
        }
      });
      
      // Debug logging removed for performance
      
      // Load campaign data
      for (const cId of campaignIds) {
        if (campaignId && cId !== campaignId) continue;
        
        const campaign = await this.getCampaign(cId);
        campaignMap.set(cId, {
          campaign,
          submissions: [],
          responses: new Map()
        });
      }
      
      // Process submissions
      submissions.forEach(submission => {
        submission.blockB.forEach(answer => {
          if (campaignId && answer.campaignId !== campaignId) return;
          
          const campaignData = campaignMap.get(answer.campaignId);
          if (campaignData) {
            campaignData.submissions.push(submission);
            
            if (!campaignData.responses.has(answer.questionId)) {
              campaignData.responses.set(answer.questionId, []);
            }
            
            campaignData.responses.get(answer.questionId)!.push({
              studentId: submission.studentId,
              answer: answer.answer,
              submittedAt: submission.submittedAt
            });
          }
        });
      });
      
      // Build analytics
      const analytics: CampaignAnalytics[] = [];
      
      for (const [cId, data] of campaignMap.entries()) {
        if (!data.campaign) continue;
        
        const totalExposures = data.submissions.length;
        const totalAnswers = Array.from(data.responses.values()).reduce((sum, responses) => sum + responses.length, 0);
        
        // Calculate answer distribution for multiple choice questions
        const answerDistribution: Record<string, number> = {};
        data.responses.forEach((responses, questionId) => {
          const question = data.campaign!.questions.find(q => q.questionId === questionId);
          if (question && question.type === 'multiple_choice') {
            responses.forEach(response => {
              answerDistribution[response.answer] = (answerDistribution[response.answer] || 0) + 1;
            });
          }
        });
        
        const campaignAnalytics: CampaignAnalytics = {
          campaignId: cId,
          campaignName: data.campaign.name,
          totalExposures,
          totalAnswers,
          answerDistribution,
          responses: Array.from(data.responses.entries()).map(([questionId, responses]) => {
            const question = data.campaign!.questions.find(q => q.questionId === questionId);
            return {
              questionId,
              type: question?.type || 'text',
              answers: responses
            };
          })
        };
        
        analytics.push(campaignAnalytics);
      }
      
      return analytics;
    } catch (error) {
      console.error('Error getting campaign analytics:', error);
      return [];
    }
  }

  // Get campaigns for a specific squad
  static async getSquadCampaigns(squadId: string): Promise<Campaign[]> {
    try {
      const campaignsRef = collection(db, 'campaigns');
      const q = query(
        campaignsRef,
        where('squadIds', 'array-contains', squadId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Campaign));
    } catch (error) {
      console.error('Error getting squad campaigns:', error);
      return [];
    }
  }

  // Toggle campaign active status
  static async toggleCampaignStatus(campaignId: string, active: boolean): Promise<boolean> {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        active,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error toggling campaign status:', error);
      return false;
    }
  }
}